export function roundHalfUp(value, decimals = 0) {
    const factor = 10 ** decimals;
    return Math.floor(value * factor + 0.5) / factor;
}
export function roundTo(value, decimals) {
    return roundHalfUp(value, decimals);
}
export function isoDate(value, timeZone = 'Asia/Seoul') {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date(value));
}
export function dateParts(date) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match)
        throw new Error(`Invalid calendar date: ${date}`);
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
export function addCalendarDays(date, amount) {
    const { year, month, day } = dateParts(date);
    const d = new Date(Date.UTC(year, month - 1, day + amount));
    return d.toISOString().slice(0, 10);
}
export function compareDate(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
export function daysBetweenInclusive(from, to) {
    const a = dateParts(from);
    const b = dateParts(to);
    return Math.floor((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000) + 1;
}
function partsForEpoch(epoch, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(epoch));
    return Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, Number(p.value)]));
}
function localKey(parts) {
    return Date.UTC(parts.year ?? 0, (parts.month ?? 1) - 1, parts.day ?? 1, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0);
}
/** Convert a wall-clock time to an epoch without shipping a timezone database. */
export function zonedTimeToEpoch(localDate, localTime, timeZone) {
    const { year, month, day } = dateParts(localDate);
    const time = /^(\d{2}):(\d{2})$/.exec(localTime);
    if (!time)
        throw new Error(`Invalid local time: ${localTime}`);
    const hour = Number(time[1]);
    const minute = Number(time[2]);
    if (hour > 23 || minute > 59)
        throw new Error(`Invalid local time: ${localTime}`);
    const wanted = Date.UTC(year, month - 1, day, hour, minute);
    const initial = partsForEpoch(wanted, timeZone);
    const offset = wanted - localKey(initial);
    let candidate = wanted + offset;
    const exact = partsForEpoch(candidate, timeZone);
    if (localKey(exact) === wanted)
        return candidate;
    // DST spring-forward can erase a wall-clock minute. Move to the first valid minute.
    for (let i = 1; i <= 180; i += 1) {
        const next = candidate + i * 60_000;
        if (localKey(partsForEpoch(next, timeZone)) >= wanted)
            return next;
    }
    return candidate;
}
export function weekdayForDate(date) {
    const { year, month, day } = dateParts(date);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
export function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : roundHalfUp((sorted[middle - 1] + sorted[middle]) / 2, 1);
}
